// GENERATED from messaging-platform/packages/chat-core/src/chat-cache.ts. Do not edit here: edit there, then run scripts/sync-chat-core.cjs.
import type { AttachmentView, ConversationSummary, ThreadMessage } from './hub-types';

/**
 * What chat keeps on the device (2026-09-20), so it is not blank or forgetful when the network is not there:
 *
 *   conversations   the last list the hub sent, shown at once on load and while reconnecting
 *   threads         the latest page of each conversation that was open, so history reads offline
 *   queued          messages typed while disconnected, sent when the connection comes back (survives a reload)
 *   drafts          half-written text per conversation
 *
 * localStorage, per app origin and per person (the key carries the principal id, so a shared computer never shows one
 * person's chat to the next). Everything here is a CACHE: the hub remains the truth, and anything unreadable is thrown
 * away rather than repaired. Bodies sit in localStorage in the clear, as they must to be shown; signing out clears it.
 */

const VERSION = 1;
/** Enough for a screenful and a scroll, not a whole history. */
const MAX_MESSAGES_PER_THREAD = 50;
const MAX_THREADS = 20;
/** localStorage is small (about 5 MB); a cache that cannot fit is simply not written. */
const MAX_BYTES = 2_000_000;

export interface QueuedMessage {
  conversationId: string;
  clientMessageId: string;
  body: string;
  replyToMessageId?: string | null;
  mentions?: string[];
  /** patient mentions, as send() takes them */
  patients?: { patientRef: string; title: string; system?: 'clinical' | 'connect' }[];
  /** files already uploaded to the hub */
  attachments?: AttachmentView[];
  /** files queued before whole file records were kept: ids only */
  attachmentIds?: string[];
  at: string;
}

interface CacheShape {
  version: number;
  conversations: ConversationSummary[];
  threads: Record<string, ThreadMessage[]>;
  queued: QueuedMessage[];
  drafts: Record<string, string>;
}

const EMPTY: CacheShape = { version: VERSION, conversations: [], threads: {}, queued: [], drafts: {} };

/** A storage that may not exist (server-side rendering, a private window, blocked site data). */
function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export class ChatCache {
  private key: string | null = null;
  private data: CacheShape = { ...EMPTY };

  /**
   * Points the cache at one person; their stored chat is loaded. Called whenever the hub says who this is — including
   * on every reconnect, which must NOT throw away a queue this page is still holding, so the same person is a no-op.
   */
  open(principalId: string | null | undefined): void {
    const key = principalId ? `chat.cache.${principalId}` : null;
    if (key && key === this.key) return;
    this.key = key;
    this.data = { ...EMPTY, threads: {}, queued: [], drafts: {} };
    const store = storage();
    if (!this.key || !store) return;
    try {
      const raw = store.getItem(this.key);
      const parsed = raw ? (JSON.parse(raw) as CacheShape) : null;
      if (parsed && parsed.version === VERSION) {
        this.data = {
          version: VERSION,
          conversations: Array.isArray(parsed.conversations) ? parsed.conversations : [],
          threads: parsed.threads && typeof parsed.threads === 'object' ? parsed.threads : {},
          queued: Array.isArray(parsed.queued) ? parsed.queued : [],
          drafts: parsed.drafts && typeof parsed.drafts === 'object' ? parsed.drafts : {},
        };
      } else if (raw) {
        store.removeItem(this.key);
      }
    } catch {
      this.data = { ...EMPTY, threads: {}, queued: [], drafts: {} };
    }
  }

  get conversations(): ConversationSummary[] {
    return this.data.conversations;
  }

  threadOf(conversationId: string): ThreadMessage[] {
    return this.data.threads[conversationId] ?? [];
  }

  get queued(): QueuedMessage[] {
    return this.data.queued;
  }

  draftOf(conversationId: string): string {
    return this.data.drafts[conversationId] ?? '';
  }

  saveConversations(list: readonly ConversationSummary[]): void {
    this.data.conversations = [...list];
    this.flush();
  }

  saveThread(conversationId: string, messages: readonly ThreadMessage[]): void {
    // Only what the hub has accepted: a queued message is kept in `queued`, not twice.
    const keep = messages.filter((m) => !m.pending && !m.failed).slice(-MAX_MESSAGES_PER_THREAD);
    if (!keep.length) delete this.data.threads[conversationId];
    else this.data.threads[conversationId] = keep;
    const ids = Object.keys(this.data.threads);
    for (const id of ids.slice(0, Math.max(0, ids.length - MAX_THREADS))) delete this.data.threads[id];
    this.flush();
  }

  saveDraft(conversationId: string, text: string): void {
    if (text.trim()) this.data.drafts[conversationId] = text;
    else delete this.data.drafts[conversationId];
    this.flush();
  }

  enqueue(message: QueuedMessage): void {
    this.data.queued = [...this.data.queued.filter((q) => q.clientMessageId !== message.clientMessageId), message];
    this.flush();
  }

  dequeue(clientMessageId: string): void {
    this.data.queued = this.data.queued.filter((q) => q.clientMessageId !== clientMessageId);
    this.flush();
  }

  /** Signing out on this device: nothing of this person's chat is left behind. */
  clear(): void {
    const store = storage();
    if (this.key && store) {
      try {
        store.removeItem(this.key);
      } catch {
        /* a full or blocked storage is not worth an error */
      }
    }
    this.data = { ...EMPTY, threads: {}, queued: [], drafts: {} };
  }

  private flush(): void {
    const store = storage();
    if (!this.key || !store) return;
    try {
      const text = JSON.stringify(this.data);
      if (text.length > MAX_BYTES) {
        // Too much to keep: the messages go, what was typed stays.
        this.data.threads = {};
        store.setItem(this.key, JSON.stringify(this.data));
        return;
      }
      store.setItem(this.key, text);
    } catch {
      /* out of room or blocked: the cache is a convenience, never a requirement */
    }
  }
}
